import { Constants, DuplicateInfoDto, FORM_ID, PhoneInput, ScrollableAlert, SinInput, useGetActiveCreditAppLinkedToCellPhoneOrEmail } from '@applyonline/shared'
import { helpTip, removeSinFormat, sinFormat, useFormAutoSave } from '@applyonline/shared/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { differenceInYears } from '@if/core/date'
import { EFinancingProgram, EGender, ELanguage } from '@if/core/types'
import { range } from 'lodash-es'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  PersonalInformationInput,
  PersonalInformationInputSchema,
} from '../../../types/Schemas/CreditApplicationSchema'

interface Props {
  creditApplicationData: PersonalInformationInput
  isCoapplicant?: boolean
  onCreditApplicationChanged: (data: PersonalInformationInput) => void
  nextStepAfter: (currentStep: string) => void
  currentStep: string
}

const fixNumberInTwoDigits = (num: number) => {
  return (num < 10 ? '0' : '') + num
}

function calculateAge(year: number | undefined, month: number | undefined, day: number | undefined) {
  let result = false
  if (year && month && day) {
    const datestring = `${year.toString()}-${month.toString()}-${day.toString()}`
    const date = new Date(datestring)
    const age = differenceInYears(new Date(), date)
    if (age >= 75) result = true
  }
  return result
}

const PersonalInformationForm = ({
  isCoapplicant = false,
  creditApplicationData,
  onCreditApplicationChanged,
  nextStepAfter,
  currentStep,
}: Props) => {
  const { t, i18n } = useTranslation()

  const [birthDate, setBirthDate] = useState<Record<string, number | undefined>>({
    year: undefined,
    month: undefined,
    day: undefined,
  })

  const [hasDuplicateLoanRequest, setHasDuplicateLoanRequest] = useState(false)

  const [activeCreditAppLinkedToCellPhoneOrEmail] = useGetActiveCreditAppLinkedToCellPhoneOrEmail(EFinancingProgram.MechanicalWarranty)

  const isOver75 = useMemo(
    () => calculateAge(birthDate.year, birthDate.month, birthDate.day),
    [birthDate.day, birthDate.month, birthDate.year],
  )
  const [daysInMonth, setDaysInMonth] = useState(31)

  const validationSchema = PersonalInformationInputSchema()
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    getValues,
    control,
    subscribe,
  } = useForm<PersonalInformationInput>({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: creditApplicationData,
  })

  useFormAutoSave(subscribe, onCreditApplicationChanged)

  const watchBirthDate = watch('applicant.birthDate')

  useEffect(() => {
    helpTip()
  }, [])

  useEffect(() => {
    const d = watchBirthDate?.split('-') ?? ['', '', '']
    if (watchBirthDate)
      setBirthDate({
        year: Number(d[0]),
        month: Number(d[1]),
        day: Number(d[2]),
      })
  }, [watchBirthDate])

  const handleDate = (event: ChangeEvent<HTMLSelectElement>) => {
    setBirthDate((birth) => {
      const { value } = event.target
      switch (event.target.id) {
        case 'month':
          birth.month = +value
          break
        case 'year':
          birth.year = +value
          break
        default:
          birth.day = +value
      }

      if (birth.year !== undefined && birth.month !== undefined) {
        const days = new Date(+birth.year, birth.month, 0).getDate()
        setDaysInMonth(days)
        if (birth.day !== undefined && birth.day > days) {
          birth.day = undefined
        }
      }

      if (!Object.values(birth).includes(undefined)) {
        setValue(
          'applicant.birthDate',
          `${birth.year}-${fixNumberInTwoDigits(Number(birth.month))}-${fixNumberInTwoDigits(Number(birth.day))}`,
        )
      } else {
        setValue('applicant.birthDate', '')
      }

      return birth
    })
  }

  const currentDate = new Date()

  const minBirthDate = {
    year: currentDate.getFullYear() - 18,
    month: currentDate.getMonth() + 1,
    day: currentDate.getDate(),
  }

  const maxBirthDate = {
    year: currentDate.getFullYear() - 100,
    month: 1,
    day: 1,
  }

  const handleSinOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const sin = removeSinFormat(value)
    setValue('applicant.sin', sin, { shouldValidate: true })
  }

  const onSubmit: SubmitHandler<PersonalInformationInput> = async (data) => {
    const dto: DuplicateInfoDto = {
      email: data.applicant.email,
      cellPhone: data.applicant.cellPhone,
    }
      const isDuplicateRequest = await activeCreditAppLinkedToCellPhoneOrEmail(dto)
      if (!isDuplicateRequest) {
        onCreditApplicationChanged(data)
        nextStepAfter(currentStep)
      } else {
        setHasDuplicateLoanRequest(true)
      }
  }

  return (
    <section className="step-content">
      <ScrollableAlert
        showAlert={
          !!(
            errors.applicant?.birthDate &&
            errors.applicant.birthDate.type !== 'required' &&
            errors.applicant.birthDate.message
          )
        }
        type="error"
        message={errors.applicant?.birthDate?.message || ''}
      />
      <ScrollableAlert
        showAlert={hasDuplicateLoanRequest}
        type="warn"
        message={t('contact.hasActiveLoanRequest')}
        link={t('contact.icebergPhone')}
        linkText={t('contact.icebergPhone')}
        linkType="tel"
      />
      <h3 className="form-question">
        {isCoapplicant ? t('personalInformation.aboutCoapplicant') : t('personalInformation.aboutYourself')}
      </h3>

      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <div className={`control-group ${errors.applicant?.languageId && 'error'}`}>
          <label htmlFor="radiobuttons">{t('personalInformation.preferredLanguage')}</label>
          <div className="radiobuttons-wrap">
            <div className="radio-wrap">
              <input
                type="radio"
                value={ELanguage.French}
                id="French"
                {...register('applicant.languageId')}
                defaultChecked={creditApplicationData.applicant.languageId === ELanguage.French}
              />
              <label htmlFor="French">{t(`ELanguage.${ELanguage.French}`, { ns: 'enum' })}</label>
            </div>
            <div className="radio-wrap">
              <input
                type="radio"
                value={ELanguage.English}
                id="English"
                {...register('applicant.languageId')}
                defaultChecked={creditApplicationData.applicant.languageId === ELanguage.English}
              />
              <label htmlFor="English">{t(`ELanguage.${ELanguage.English}`, { ns: 'enum' })}</label>
            </div>
          </div>
        </div>

        <div className={`control-group ${errors.applicant?.genderId && 'error'}`}>
          <label htmlFor="radiobuttons">{t('personalInformation.yourGender')}</label>
          <div className="radiobuttons-wrap">
            <div className="radio-wrap">
              <input
                type="radio"
                value={0}
                id="Male"
                {...register('applicant.genderId')}
                defaultChecked={creditApplicationData.applicant.genderId === EGender.Men}
              />
              <label htmlFor="Male">{t('personalInformation.male')}</label>
            </div>
            <div className="radio-wrap">
              <input
                type="radio"
                value={1}
                id="Female"
                {...register('applicant.genderId')}
                defaultChecked={creditApplicationData.applicant.genderId === EGender.Woman}
              />
              <label htmlFor="Female">{t('personalInformation.female')}</label>
            </div>
            <div className="radio-wrap">
              <input
                type="radio"
                value={2}
                id="Other"
                {...register('applicant.genderId')}
                defaultChecked={creditApplicationData.applicant.genderId === EGender.Other}
              />
              <label htmlFor="Other"> {t('personalInformation.preferNotSay')}</label>
            </div>
          </div>
        </div>

        <div className="control-group-wrap">
          <div className={`control-group third sm-full ${errors.applicant?.firstName && 'error'}`}>
            <label htmlFor="firstName">
              {t('personalInformation.yourFirstName')}
              <span className="form-hint">{t('personalInformation.nameHint')}</span>
            </label>
            <input type="text" id="firstName" {...register('applicant.firstName')} maxLength={50} />
          </div>

          <div className={`control-group third sm-full ${errors.applicant?.middleName && 'error'}`}>
            <label htmlFor="middleName">
              {t('personalInformation.yourMiddleName')}
              <span className="form-hint">{t('personalInformation.optional')}</span>
            </label>
            <input type="text" id="middleName" {...register('applicant.middleName')} maxLength={50} />
          </div>

          <div className={`control-group third sm-full ${errors.applicant?.lastName && 'error'}`}>
            <label htmlFor="lastName">
              {t('personalInformation.yourLastName')}
              <span className="form-hint">{t('personalInformation.nameHint')}</span>
            </label>
            <input type="text" id="lastName" {...register('applicant.lastName')} maxLength={50} />
          </div>
        </div>

        <div className="control-group">
          <label htmlFor="year">{t('personalInformation.yourBirthDate')}</label>
          <div className="date-wrap">
            <div className={`dropdown-wrap ${errors.applicant?.birthDate && 'error'}`}>
              <select id="year" name="date" onChange={handleDate} value={birthDate.year?.toString() ?? ''}>
                <option value="" disabled>
                  {t('personalInformation.birthYear')}
                </option>
                {range(minBirthDate.year, maxBirthDate.year).map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className={`dropdown-wrap ${errors.applicant?.birthDate && 'error'}`}>
              <select
                id="month"
                name="date"
                onChange={handleDate}
                value={birthDate.month ? fixNumberInTwoDigits(birthDate.month) : ''}
              >
                <option value="" disabled>
                  {t('personalInformation.birthMonth')}
                </option>
                {Object.keys(Constants.monthNames).map((month) => (
                  <option value={fixNumberInTwoDigits(+month)} key={Constants.monthNames[Number(month)].nameEn}>
                    {i18n.language === 'en'
                      ? Constants.monthNames[Number(month)].nameEn
                      : Constants.monthNames[Number(month)].nameFr}
                  </option>
                ))}
              </select>
            </div>
            <div className={`dropdown-wrap ${errors.applicant?.birthDate && 'error'}`}>
              <select
                id="day"
                name="date"
                onChange={handleDate}
                value={birthDate.day ? fixNumberInTwoDigits(birthDate.day) : ''}
              >
                <option value="" disabled>
                  {t('personalInformation.birthDay')}
                </option>
                {range(1, daysInMonth + 1).map((item) => (
                  <option key={item} value={fixNumberInTwoDigits(item)}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {isOver75 && (
            <div
              style={{
                border: '#08abf0 solid 3px',
                padding: '15px',
                borderRadius: '8px',
                marginTop: '2rem',
                fontWeight: 'bold',
                color: '#08abf0',
              }}
            >
              <strong>
                {isCoapplicant
                  ? t('personalInformation.cannotBeCoapplicantMessage')
                  : t('personalInformation.birthYearOver75Message')}
              </strong>
            </div>
          )}
        </div>

        <div className="control-group">
          <span className="help-tip">
            <p>{t('personalInformation.optionalSin')}</p>
          </span>
          <SinInput
            id="insurance-number"
            defaultValue={sinFormat(getValues('applicant.sin') ?? '')}
            onBlur={handleSinOnChange}
            error={errors.applicant?.sin}
          />
        </div>

        {/* Contact Information Section */}
        <h3 className="form-question" style={{ marginTop: '2rem' }}>
          {t('contact.provideInformation')}
        </h3>

        <div className="control-group-wrap" style={{ alignItems: 'start' }}>
          <PhoneInput className="mid" control={control} name="applicant.cellPhone" label={t('contact.mobilePhone')} />
          <PhoneInput className="mid" control={control} name="applicant.homePhone" label={t('contact.otherPhone')} />
        </div>

        <div className={`control-group  ${errors.applicant?.email && 'error'}`}>
          <label htmlFor="email">
            {t('contact.emailAddress')}
            <span className="help-tip">
              <p>{t('contact.emailAddressInfo')}</p>
            </span>
          </label>
          <input
            id="email"
            type="text"
            inputMode="email"
            autoComplete="email"
            {...register('applicant.email')}
            maxLength={60}
          />
          {errors.applicant?.email && <p className="error-message">{errors.applicant.email.message}</p>}
        </div>

        <div className="empty-space" />
        <div className="clarification">
          <p>
            {t('contact.newsletter')}
            <u>
              <a href={t('common.policyLink')} target="_blank" rel="noreferrer">
                {t('contact.linkText')}
              </a>
            </u>
            {t('contact.newsletterEnd')}
          </p>
        </div>
        <div className="control-group">
          <input type="checkbox" id="newsletter" {...register('applicant.subscribeNewsletter')} />
          <label htmlFor="newsletter">{t('contact.subscribe')}</label>
        </div>
      </form>
    </section>
  )
}

export default PersonalInformationForm
